import {
  FunctionComponent,
  useState,
  useRef,
  useCallback,
  ChangeEvent,
  FormEvent,
  Fragment,
} from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { Input, Button, toast, Text, Box } from '@conexasaude/conexa-hero'
import services from '../../auth-services'
import useStyles from './styles'
import { useParams } from 'react-router-dom'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'
import ClariHelper from '@/components/Clari'

const ForgotPasswordForm: FunctionComponent = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [email, setEmail] = useState('')
  const recaptchaRef = useRef<ReCAPTCHA>(null)

  const styles = useStyles()

  const { clinic } = useParams<{ clinic: string }>()

  const { logAnalyticsEvent } = useFirebaseContext()

  const redirectToActivation = useCallback(() => {
    let activationUrl = import.meta.env.VITE_APP_ACTIVATION_PAGE_URL + '/passo1'
    if (clinic) {
      activationUrl += `/${clinic}`
    }
    window.open(activationUrl, '_self')
  }, [clinic])

  const changePassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsLoading(true)

    try {
      const token = await recaptchaRef?.current?.executeAsync()

      await services?.sendChangePasswordEmail(email, token)

      logAnalyticsEvent('Pac_EsqueciSenha_RedefinirSenha', {
        response_status: 'ok',
      })

      toast({
        message:
          'Foi enviado um e-mail pra você. Verifique sua caixa de entrada.',
        variant: 'success',
        autoCloseDuration: 8000,
      })
    } catch (err) {
      if (
        err?.response?.data?.msg?.includes('STATUS_USUARIO_INATIVO_DEPENDENTE')
      ) {
        toast({
          message:
            'Para continuar, você precisa ativar sua conta ou a dos seus dependentes.',
          ...(typeof window !== 'undefined' &&
            window.innerWidth >= 639 && {
              action: redirectToActivation,
              actionLabel: 'Ativar conta',
            }),
          variant: 'warning',
          autoClose: false,
        })
        return
      }
      if (err?.response?.data?.msg?.includes('STATUS_USUARIO_INATIVO')) {
        toast({
          message:
            'Notamos que você não ativou sua conta. Para realizar ou agendar uma consulta, ative sua conta agora.',
          ...(typeof window !== 'undefined' &&
            window.innerWidth >= 639 && {
              action: redirectToActivation,
              actionLabel: 'Ativar conta',
            }),
          variant: 'warning',
          autoClose: false,
        })

        return
      }
      const errMessage =
        err?.response?.data?.object?.msg ||
        err?.response?.data?.msg ||
        err?.message

      logAnalyticsEvent('Pac_EsqueciSenha_RedefinirSenha', {
        response_status: 'error',
      })

      toast({
        message: errMessage,
        variant: 'error',
        autoCloseDuration: 5000,
      })
    } finally {
      setIsLoading(false)
      recaptchaRef?.current?.reset()
    }
  }

  const handleEmailChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setEmail(event.target.value)
    },
    [],
  )

  return (
    <Fragment>
      <form css={styles.container} onSubmit={changePassword}>
        <Box>
          <Text renderAs="h2">Esqueceu a senha?</Text>
          <Text renderAs="p">Redefina a senha em apenas dois passos.</Text>

          <Input
            label="E-mail"
            placeholder="Informe o seu e-mail"
            type="email"
            value={email}
            onChange={handleEmailChange}
            fullWidth
          />

          <Button
            id="EsqueciSenha-EnviarCódigo-CTA_Enviar"
            busy={isLoading}
            disabled={!email?.length}
            fullWidth
            css={styles.buttonWrapper}
          >
            Redefinir senha
          </Button>

          <ReCAPTCHA
            ref={recaptchaRef}
            size="invisible"
            sitekey={import.meta.env.VITE_APP_RECAPTCHA_SITE_KEY}
          />
        </Box>
      </form>

      <ClariHelper showShotcut={false} cssLink={styles.linkSupport} />
    </Fragment>
  )
}

export { ForgotPasswordForm }
